// src/views/Internet/BandwidthGraph.jsx
import * as d3 from "d3";
import React, { useEffect, useRef, useState } from "react";

// MUI v4 imports
import Box from "@material-ui/core/Box";
import FormGroup from "@material-ui/core/FormGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import { green, blue, purple, orange } from "@material-ui/core/colors";

const tickSize = 7;

const parseDate = d3.timeParse("%Y-%m-%d %H:%M:%S");

const downStreamColor = "green";
const upStreamColor = "blue";
const downStreamPacketsColor = "purple";
const upStreamPacketsColor = "orange";
const LEFT_TICK_SIZE = 30;
const MIN_MBPS_SCALE = 1;
const MIN_PACKETS_SCALE = 100;
const BYTES_MEASURE = 8 * 0.000001;
const BYTES_UNIT = "Mbp/s";
const PACKETS_MEASURE = 1;
const PACKETS_UNIT = "p/s";

function getTimeTickSize(width) {
  if (width > 1500) {
    return 15;
  }

  if (width > 1000) {
    return 30;
  }

  return 60;
}

export default function BandwidthGraph({
  downStreamData,
  upStreamData,
  downStreamPackets,
  upStreamPackets,
  height,
  width,
  showLegend = true,
  showUnits = true,
  marginTop = 20,
  marginRight = 60,
  marginBottom = 30,
  marginLeft = 40,
}) {
  const [show, setShow] = useState({
    downstreamBytes: true,
    upstreamBytes: true,
    downstreamPackets: false,
    upstreamPackets: false,
  });
  const ref = useRef();

  useEffect(() => {
    // X axis
    let leftTickSize = 0;
    if (showUnits) {
      leftTickSize = LEFT_TICK_SIZE;
    }
    const xScale = d3
      .scaleTime()
      .domain(d3.extent(upStreamData, d => parseDate(d.x)))
      .range([marginLeft, width - marginRight - leftTickSize]);

    let yScaleMax =
      d3.max(
        [
          ...(show.downstreamBytes ? downStreamData : []),
          ...(show.upstreamBytes ? upStreamData : []),
        ],
        d => parseInt(d.y, 10),
      ) * BYTES_MEASURE;

    let y2ScaleMax =
      d3.max(
        [
          ...(show.downstreamPackets ? downStreamPackets : []),
          ...(show.upstreamPackets ? upStreamPackets : []),
        ],
        d => parseInt(d.y, 10),
      ) * PACKETS_MEASURE;

    if (yScaleMax < 1) {
      yScaleMax = MIN_MBPS_SCALE;
    }

    if (y2ScaleMax < 1) {
      y2ScaleMax = MIN_PACKETS_SCALE;
    }

    const yScale = d3
      .scaleLinear()
      .domain([0, yScaleMax])
      .range([height - marginBottom, marginTop]);

    const y2Scale = d3
      .scaleLinear()
      .domain([0, y2ScaleMax])
      .range([height - marginBottom, marginTop]);

    // Bottom Y axis (for UpStream)
    const svg = d3.select(ref.current);
    // Remove previous graph
    svg.selectAll("*").remove();
    // Append new svg
    svg.attr("width", width).attr("height", height).append("g");
    // xAxis ticker values every 15/30/60 seconds
    const tickValues = xScale.ticks(
      d3.timeSecond.every(getTimeTickSize(width)),
    );
    const xAxis = d3.axisBottom(xScale);
    const yAxis = d3.axisLeft(yScale);
    const y2Axis = d3.axisRight(y2Scale);

    if (showUnits) {
      xAxis.tickValues(tickValues).tickFormat(d3.timeFormat("%H:%M:%S"));
      yAxis.ticks(tickSize).tickFormat(d => `${d} ${BYTES_UNIT}`);
      y2Axis.ticks(tickSize).tickFormat(d => `${d} ${PACKETS_UNIT}`);
    }

    // Append axes to SVG
    svg
      .append("g")
      .attr("class", "x-axis")
      .attr("transform", `translate(${leftTickSize}, ${height - marginBottom})`)
      .call(xAxis)
      .selectAll("text")
      .attr("fill", "white");

    svg
      .append("g")
      .attr("class", "y-axis")
      .attr("transform", `translate(${marginLeft + leftTickSize}, 0)`)
      .call(yAxis)
      .selectAll("text")
      .attr("fill", "white");

    svg
      .append("g")
      .attr("class", "y2-axis")
      .attr("transform", `translate(${width - marginRight}, 0)`)
      .call(y2Axis)
      .selectAll("text")
      .attr("fill", "white");

    // extend the y-axis tick lines
    svg
      .selectAll(".y-axis .tick line")
      .attr("x1", width - marginLeft - marginRight - leftTickSize)
      .attr("stroke", "#666");
    svg
      .selectAll(".y-axis-bottom-left .tick line")
      .attr("x1", width - marginLeft - marginRight - leftTickSize)
      .attr("stroke", "#666");

    // Make all axis lines grey
    svg.selectAll(".domain").attr("stroke", "#666");
    svg.selectAll(".tick line").attr("stroke", "#666");

    if (showLegend) {
      const legendData = ["DownStream", "UpStream"];
      const legendSize = 91;

      // Create legend SVG
      const legend = svg
        .append("g")
        .attr("class", "legend")
        .attr(
          "transform",
          `translate(${width - marginRight - legendSize}, ${marginTop})`,
        );

      // Add legend items
      const legendItems = legend
        .selectAll(".legend-item")
        .data(legendData)
        .enter()
        .append("g")
        .attr("class", "legend-item")
        .attr("transform", (d, i) => "translate(0," + i * 20 + ")");

      // Add colored rectangles
      legendItems
        .append("rect")
        .attr("x", 0)
        .attr("y", 6)
        .attr("width", 10)
        .attr("height", 10)
        .style("fill", (d, i) => [downStreamColor, upStreamColor][i]);

      // Add text labels
      legendItems
        .append("text")
        .attr("x", 20)
        .attr("y", 10)
        .attr("fill", "white")
        .attr("dy", ".35em")
        .text(d => d);
    }

    const area = d3
      .area()
      .x(d => xScale(parseDate(d.x)))
      .y0(height - marginBottom)
      .y1(d => yScale(parseInt(d.y, 10) * BYTES_MEASURE));

    const line = d3
      .line()
      .x(d => xScale(parseDate(d.x)))
      .y(d => yScale(parseInt(d.y, 10) * BYTES_MEASURE));

    const area2 = d3
      .area()
      .x(d => xScale(parseDate(d.x)))
      .y0(height - marginBottom)
      .y1(d => y2Scale(parseInt(d.y, 10) * PACKETS_MEASURE));

    const line2 = d3
      .line()
      .x(d => xScale(parseDate(d.x)))
      .y(d => y2Scale(parseInt(d.y, 10) * PACKETS_MEASURE));

    if (show.downstreamBytes) {
      svg
        .append("path")
        .datum(downStreamData)
        .attr("fill", "none")
        .attr("stroke", downStreamColor)
        .attr("stroke-width", 2)
        .attr("transform", `translate(${leftTickSize}, 0)`)
        .attr("d", line);

      svg
        .append("path")
        .datum(downStreamData)
        .attr("fill", downStreamColor)
        .attr("opacity", 0.3)
        .attr("transform", `translate(${leftTickSize}, 0)`)
        .attr("d", area);
    }

    if (show.upstreamBytes) {
      svg
        .append("path")
        .datum(upStreamData)
        .attr("fill", upStreamColor)
        .attr("opacity", 0.3)
        .attr("transform", `translate(${leftTickSize}, 0)`)
        .attr("d", area);

      svg
        .append("path")
        .datum(upStreamData)
        .attr("fill", "none")
        .attr("stroke", upStreamColor)
        .attr("stroke-width", 2)
        .attr("transform", `translate(${leftTickSize}, 0)`)
        .attr("d", line);
    }

    if (show.downstreamPackets) {
      svg
        .append("path")
        .datum(downStreamPackets)
        .attr("fill", "none")
        .attr("stroke", downStreamPacketsColor)
        .attr("stroke-width", 2)
        .attr("transform", `translate(${leftTickSize}, 0)`)
        .attr("d", line2);

      svg
        .append("path")
        .datum(downStreamPackets)
        .attr("fill", downStreamPacketsColor)
        .attr("opacity", 0.3)
        .attr("transform", `translate(${leftTickSize}, 0)`)
        .attr("d", area2);
    }

    if (show.upstreamPackets) {
      svg
        .append("path")
        .datum(upStreamPackets)
        .attr("fill", "none")
        .attr("stroke", upStreamPacketsColor)
        .attr("stroke-width", 2)
        .attr("transform", `translate(${leftTickSize}, 0)`)
        .attr("d", line2);

      svg
        .append("path")
        .datum(upStreamPackets)
        .attr("fill", upStreamPacketsColor)
        .attr("opacity", 0.3)
        .attr("transform", `translate(${leftTickSize}, 0)`)
        .attr("d", area2);
    }
  }, [
    upStreamData,
    downStreamData,
    downStreamPackets,
    upStreamPackets,
    show,
    height,
    width,
    showLegend,
    showUnits,
    marginTop,
    marginRight,
    marginBottom,
    marginLeft,
  ]);

  return (
    <Box display="flex" flexDirection="column">
      <svg
        style={{ backgroundColor: "inherit", display: "block" }}
        id="linechart"
        ref={ref}
        width={width}
        height={height}
      ></svg>
      <FormGroup style={{ marginLeft: "1vw" }} row>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              style={{
                padding: 3,
                color: green[800],
              }}
              checked={show.downstreamBytes}
              onChange={() =>
                setShow(prev => ({
                  ...prev,
                  downstreamBytes: !prev.downstreamBytes,
                }))
              }
            />
          }
          label="Downstream"
        />
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              style={{
                padding: 3,
                color: blue[800],
              }}
              checked={show.upstreamBytes}
              onChange={() =>
                setShow(prev => ({
                  ...prev,
                  upstreamBytes: !prev.upstreamBytes,
                }))
              }
            />
          }
          label="Upstream"
        />
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              style={{
                padding: 3,
                color: purple[800],
              }}
              checked={show.downstreamPackets}
              onChange={() =>
                setShow(prev => ({
                  ...prev,
                  downstreamPackets: !prev.downstreamPackets,
                }))
              }
            />
          }
          label="Downstream Packets"
        />
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              style={{
                padding: 3,
                color: orange[800],
              }}
              checked={show.upstreamPackets}
              onChange={() =>
                setShow(prev => ({
                  ...prev,
                  upstreamPackets: !prev.upstreamPackets,
                }))
              }
            />
          }
          label="Upstream Packets"
        />
      </FormGroup>
    </Box>
  );
}